import axios from 'axios';
// import groupBy from 'lodash/groupBy';
import cloneDeep from 'lodash/cloneDeep';
import {
    REVIEWS_WIDGET_ID,
    TREND_SENTIMENT_WIDGET_ID,
    SENTIMENT_WIDGET_ID,
    DEMOGRAPHICS_WIDGET_ID,
    KEYWORDS_CLOUD_WIDGET_ID,
    FIRST_REPLY_TIME_WIDGET_ID,
    NETWORK_BREAKDOWN_WIDGET_ID,
    CONVERSATION_THEMES_ID,
    TRENDING_KEYWORDS_WIDGET_ID,
    MOST_IMPACTFUL_POSTS_WIDGET_ID,
    TRENDING_LABELS_WIDGET_ID
} from '@/assets/dashboard/widget-ids';
import toCamelCase from '@/helpers/toCamelCase';
import parseValueIfNumber from '@/helpers/parseValueIfNumber';
import socialMediaConfig from '@/assets/common/social-media-config';
import toSnakeCaseThenStringify from '@/helpers/toSnakeCaseThenStringify';
import pinRangeToExecutedWindow from '@/helpers/dashboard/pinRangeToExecutedWindow';
import { TOP_LANGUAGES_OPTION, BY_TOTAL_ENGAGEMENT_VALUE } from '@/assets/dashboard/widget-settings';
import { NEUTRAL_SENTIMENT, NEGATIVE_SENTIMENT, POSITIVE_SENTIMENT } from '@/assets/common/sentiments';
import { saveState, getSavedState } from '@/helpers/storageOperations';

export const state = () => ({
    templates: [],
    categories: {},
    sections: {},
    timeInUnix: [], // @NOTE [startDate, endDate] in unix format (UTC)  
    allContexts: [],
    selectedContexts: [],
    previousTimeInUnix: [],
    compactLayout: getSavedState('dashboard.compactLayout') || false,
    pageTemplate: {
        id: null,
        name: null,
        time: 'last-30-days',
        timeUnit: 'days',
        config: {
            contextIds: [],
            widgets: [
                {
                    id: SENTIMENT_WIDGET_ID,
                    config: {
                        areAnnotationsEnabled: true,
                        layerIds: [],
                        disabledDatasets: ['Neutral']
                    }
                },
                {
                    id: TREND_SENTIMENT_WIDGET_ID,
                    config: {
                        areAnnotationsEnabled: false,
                        layerIds: [],
                        disabledDatasets: ['Neutral', 'Shares', 'Reach']
                    }
                },
                {
                    id: NETWORK_BREAKDOWN_WIDGET_ID,
                    config: {
                        selectedContextId: null,
                        activeTab: 'all',
                        disabledNetworks: []
                    }
                },
                {
                    id: KEYWORDS_CLOUD_WIDGET_ID,
                    config: {
                        selectedSentiments: [POSITIVE_SENTIMENT, NEGATIVE_SENTIMENT, NEUTRAL_SENTIMENT],
                        keywordTrendingChart: {
                            disabledDatasets: ['Neutral']
                        }
                    }
                },
                {
                    id: TRENDING_KEYWORDS_WIDGET_ID,
                    config: {
                        search: ''
                    }
                },
                {
                    id: CONVERSATION_THEMES_ID,
                    config: {
                        search: ''
                    }
                },
                {
                    id: TRENDING_LABELS_WIDGET_ID,
                    config: {
                        search: ''
                    }
                },
                {
                    id: REVIEWS_WIDGET_ID,
                    config: {
                        selectedSocialMediaFilters: [
                            socialMediaConfig.trustpilot.id,
                            socialMediaConfig.googlePlay.id,
                            socialMediaConfig.ios.id
                        ]
                    }
                },
                {
                    id: DEMOGRAPHICS_WIDGET_ID,
                    config: {
                        mode: TOP_LANGUAGES_OPTION
                    }
                },
                {
                    id: MOST_IMPACTFUL_POSTS_WIDGET_ID,
                    config: {
                        selectedSentiments: [POSITIVE_SENTIMENT, NEUTRAL_SENTIMENT, NEGATIVE_SENTIMENT],
                        selectedSortOption: BY_TOTAL_ENGAGEMENT_VALUE,
                        selectedSocialMediaFilters: [socialMediaConfig.instagram.id, socialMediaConfig.twitter.id]
                    }
                },
                {
                    id: FIRST_REPLY_TIME_WIDGET_ID,
                    config: {
                        selectedContextId: null,
                        activeMode: 'TIME_AVERAGE_MODE',
                        disabledDatasets: []
                    }
                }
            ]
        }
    },
    selectedContextsLimit: 3,
    selectedContextsColors: ['#1890ff', '#b222d7', '#efac2d'],
    totalPosts: {},
    totalPostsPrev: {},
    sharesCount: {},
    sharesCountPrev: {},
    engagementsCount: {},
    engagementsCountPrev: {},
    reachScore: {},
    reachScorePrev: {}
});

export const getters = {
    allContexts: state => state.allContexts,
    brandContexts: state => {
        const filteredBrands = state.allContexts.filter(({ section }) => section === 'brands');

        let categorizedBrands = {}

        filteredBrands.forEach(item => {
            let key = item.feedCursor ? item.feedCursor.opts.contextId : item.contextId;
        
            if (!categorizedBrands[key]) {
                categorizedBrands[key] = [];
            }

            
        
            categorizedBrands[key].push(item);
        });

        return Object.keys(categorizedBrands).map(key => {
            return {
                id: key,
                title: state.categories[key]?.label || '<missing label>',
                image: state.categories[key]?.image || '',
                networks: categorizedBrands[key],
                conversationClusterEnabled: (categorizedBrands[key] || [])[0]?.conversationClusterEnabled,
                section: state.categories[key]?.section
            }
        });
    },
    accountContexts: state => state.allContexts.filter(({ section }) => section === 'my_searches'),
    teamContexts: state => state.allContexts.filter(({ section }) => section === 'team_searches'),
    templates: state => state.templates,
    timeInUnix: state => state.timeInUnix,
    previousTimeInUnix: state => state.previousTimeInUnix,
    compactLayout: state => state.compactLayout,
    pageTemplate: state => state.pageTemplate,
    selectedContexts: state => cloneDeep(state.selectedContexts).map(selectedContext => {
        (selectedContext.properties || []).map(property => {
            if (property.filters.range && JSON.stringify(property.filters.range) !== JSON.stringify({})) {
                property.filters.range['shiftEndDate'] = true;
            }

            return property;
        });

        return selectedContext;
    }),
    isCompareModeActive: state => state.selectedContexts.length > 1,
    selectedContextsColors: state => state.selectedContextsColors,
    selectedContextsLimit: state => state.selectedContextsLimit,
    totalPosts: state => state.totalPosts,
    totalPostsPrev: state => state.totalPostsPrev,
    categories: state => state.categories,
    sections: state => state.sections,
    sharesCount: state => state.sharesCount,
    sharesCountPrev: state => state.sharesCountPrev,
    engagementsCount: state => state.engagementsCount,
    engagementsCountPrev: state => state.engagementsCountPrev,
    reachScore: state => state.reachScore,
    reachScorePrev: state => state.reachScorePrev
};

export const mutations = {
    SET_TEMPLATES(state, templates) {
        state.templates = templates;
    },

    SET_TIME_IN_UNIX(state, timeInUnix) {
        state.timeInUnix = timeInUnix;
    },

    SET_PREVIOUS_TIME_IN_UNIX(state, previousTimeInUnix) {
        state.previousTimeInUnix = previousTimeInUnix;
    },

    SET_COMPACT_LAYOUT(state, compactLayout) {
        saveState('dashboard.compactLayout', compactLayout);
        state.compactLayout = compactLayout;
    },

    SET_ALL_CONTEXTS(state, allContexts) {
        state.allContexts = allContexts;
    },

    SET_CATEGORIES(state, categories) {
        state.categories = categories;
    },

    SET_SECTIONS(state, sections) {
        state.sections = sections;
    },

    ADD_CONTEXT(state, id) {
        console.log('add context', id)
        const option = state.allContexts.find(item => item.id === id);

        const color = state.selectedContextsColors[state.selectedContexts.length];

        state.selectedContexts.push({ ...option, color });
    },

    UPDATE_CONTEXT(state, { id, index }) {
        console.log('update context', id , index)
        const color = state.selectedContextsColors[index];
        const option = state.allContexts.find(item => item.id === id);

        // @TODO
        state.selectedContexts.splice(index, 1, { ...option, color });
        state.selectedContexts = cloneDeep(state.selectedContexts);
    },

    REMOVE_CONTEXT(state, indexToRemove) {
        state.selectedContexts.splice(indexToRemove, 1);

        state.selectedContexts = state.selectedContexts.map((selectedContext, index) => ({
            ...cloneDeep(selectedContext),
            color: state.selectedContextsColors[index]
        }));
    },

    OVERWRITE_CONTEXTS(state, contextIds) {
        state.selectedContexts = contextIds.map((id, index) => {
            const option = state.allContexts.find(item => item.id === parseValueIfNumber(id));
            const color = state.selectedContextsColors[index];

            return { ...option, color };
        });
    },

    SET_UNSAVED_SEARCH(state, unsavedSearchContext) {
        const { contextId, query, filters } = unsavedSearchContext;
        const search = {
            id: 'unsavedSearch',
            title: 'Unsaved search',
            contextId: contextId,
            contextType: 6, // VIRTUAL
            properties: [
                {
                    query,
                    filters
                }
            ],
            ...unsavedSearchContext
        };

        state.allContexts.push(search);
    },

    SET_PAGE_TEMPLATE(state, pageTemplate) {
        state.pageTemplate = pageTemplate;
    },

    UPDATE_PAGE_TEMPLATE(state, { id, name, time, timeUnit, widgetId, contextIds, updatedConfig = {} }) {
        if (id || id === null) {
            state.pageTemplate.id = id;
        }

        if (name || name === null) {
            state.pageTemplate.name = name;
        }

        if (time) {
            state.pageTemplate.time = time;
        }

        if (timeUnit) {
            state.pageTemplate.timeUnit = timeUnit;
        }

        if (contextIds) {
            state.pageTemplate.config.contextIds = contextIds;
        }

        if (!widgetId) {
            return;
        }

        const widgetIndex = state.pageTemplate.config.widgets.findIndex(widget => widget.id === widgetId);

        if (~widgetIndex) {
            const currentConfig = state.pageTemplate.config.widgets[widgetIndex].config;

            state.pageTemplate.config.widgets[widgetIndex].config = {
                ...currentConfig,
                ...updatedConfig
            };

            return;
        }

        state.pageTemplate.config.widgets.push({
            id: widgetId,
            config: updatedConfig
        });
    },

    SET_TOTAL_POSTS(state, { id, totalPosts }) {
        state.totalPosts[id] = totalPosts;
    },

    SET_TOTAL_POSTS_PREV(state, { id, totalPostsPrev }) {
        state.totalPostsPrev[id] = totalPostsPrev;
    },

    SET_SHARES_COUNT(state, { id, sharesCount }) {
        state.sharesCount[id] = sharesCount;
    },

    SET_SHARES_COUNT_PREV(state, { id, sharesCountPrev }) {
        state.sharesCountPrev[id] = sharesCountPrev;
    },

    SET_ENGAGEMENTS_COUNT(state, { id, engagementsCount }) {
        state.engagementsCount[id] = engagementsCount;
    },

    SET_ENGAGEMENTS_COUNT_PREV(state, { id, engagementsCountPrev }) {
        state.engagementsCountPrev[id] = engagementsCountPrev;
    },

    SET_REACH_SCORE(state, { id, reachScore }) {
        state.reachScore[id] = reachScore;
    },

    SET_REACH_SCORE_PREV(state, { id, reachScorePrev }) {
        state.reachScorePrev[id] = reachScorePrev;
    },

    SET_SELECTED_CONTEXTS(state, { selectedContexts }) {
        state.selectedContexts = selectedContexts;
    }
};

export const actions = {
    setTimeInUnix({ commit }, timeInUnix) {
        commit('SET_TIME_IN_UNIX', timeInUnix);
    },

    setPreviousTimeInUnix({ commit }, previousTimeInUnix) {
        commit('SET_PREVIOUS_TIME_IN_UNIX', previousTimeInUnix);
    },

    setCompactLayout({ commit }, compactLayout) {
        commit('SET_COMPACT_LAYOUT', compactLayout);
    },

    addContext({ commit }, id) {
        commit('ADD_CONTEXT', id);
    },

    updateContext({ commit }, { id, index }) {
        commit('UPDATE_CONTEXT', { id, index });
    },

    removeContext({ commit }, index) { 
        commit('REMOVE_CONTEXT', index);
    },

    overwriteContexts({ commit }, contextIds) {
        commit('OVERWRITE_CONTEXTS', contextIds);
    },

    setPageTemplate({ commit }, template) {
        commit('SET_PAGE_TEMPLATE', template);

        commit('OVERWRITE_CONTEXTS', template.config.contextIds);
    },

    updatePageTemplate({ commit }, data) {
        commit('UPDATE_PAGE_TEMPLATE', data);
    },

    async getCustomContextSelectorData({ commit }) {
        const { data } = await axios.getMethodOverridePost('v1/dashboard_selector');

        const { contexts } = toCamelCase(data);

        commit('SET_ALL_CONTEXTS', contexts);

        commit('SET_CATEGORIES', data.categories);

        commit('SET_SECTIONS', data.sections);
    },

    async getDashboardTemplates({ commit }) {
        const templates = await axios.$get('/v1/dashboard/templates');

        if (templates && Array.isArray(templates)) {
            commit('SET_TEMPLATES', templates);

            return templates;
        }

        return [];
    },

    getDashboardTemplate(vuexContext, id) {
        return axios.$get(`/v1/dashboard/templates/${id}`);
    },

    storeDashboardTemplate(vuexContext, payload) {
        return axios.$post('/v1/dashboard/templates', payload);
    },

    updateDashboardTemplate(vuexContext, payload) {
        return axios.$patch(`/v1/dashboard/templates/${payload.id}`, payload);
    },

    async loadUnsavedSearch({ commit }, unsavedSearchHash) {
        const params = {
            feed_hash: unsavedSearchHash
        };
        const context = await axios.$get('/v1/dashboard/feeds/hash', { params });
        commit(
            'SET_UNSAVED_SEARCH',
            {
                ...context.compiledProperties,
                contextId: unsavedSearchHash,
                virtualTitle: context.cursorData?.opts?.title
            }
        );
    },

    async getHash(
        vuexContext,
        { range, title, viewType, accountId, extraParams, selectedContext, executedWindow = null }
    ) {
        const { contextId, contextType, properties } = selectedContext;

        // No range for a Helper chat on a brand alone: its questions pick their own
        const [minCreatedAt, maxCreatedAt] = range || [];

        const payload = {
            title,
            viewType,
            accountId,
            contextId,
            contextType,
            minCreatedAt,
            maxCreatedAt,
            properties: toSnakeCaseThenStringify(pinRangeToExecutedWindow(properties, executedWindow)),
            ...extraParams
        };

        const { id } = await axios.$post('/v1/dashboard/feeds/hash', payload);

        return id;
    },

    async getProperties(
        vuexContext,
        hash
    ) {
        const params = {
            feedHash: hash
        }
        const properties = await axios.$get(`/v1/dashboard/feeds/hash`, {params});

        return properties;
    },

    setTotalPosts({ commit }, { id, totalPosts }) {
        commit('SET_TOTAL_POSTS', { id, totalPosts });
    },

    setTotalPostsPrev({ commit }, { id, totalPostsPrev }) {
        commit('SET_TOTAL_POSTS_PREV', { id, totalPostsPrev });
    },

    setSharesCount({ commit }, { id, sharesCount }) {
        commit('SET_SHARES_COUNT', { id, sharesCount });
    },

    setSharesCountPrev({ commit }, { id, sharesCountPrev }) {
        commit('SET_SHARES_COUNT_PREV', { id, sharesCountPrev });
    },

    setEngagementsCount({ commit }, { id, engagementsCount }) {
        commit('SET_ENGAGEMENTS_COUNT', { id, engagementsCount });
    },

    setEngagementsCountPrev({ commit }, { id, engagementsCountPrev }) {
        commit('SET_ENGAGEMENTS_COUNT_PREV', { id, engagementsCountPrev });
    },

    setReachScore({ commit }, { id, reachScore }) {
        commit('SET_REACH_SCORE', { id, reachScore });
    },

    setReachScorePrev({ commit }, { id, reachScorePrev }) {
        commit('SET_REACH_SCORE_PREV', { id, reachScorePrev });
    },

    setSelectedContexts({ commit }, { selectedContexts }) {
        commit('SET_SELECTED_CONTEXTS', { selectedContexts });
    }
};
